import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@communitly/shared';
import { useTranslation } from 'react-i18next';

export const useBadges = (communityId: string) => {
  const { i18n } = useTranslation();
  const currentLocale = i18n.language;

  return useQuery<Badge[]>({
    queryKey: ['badges', communityId, currentLocale],
    queryFn: async () => {
      // Fetch badges with translation data
      const { data: badgeData, error } = await supabase
        .from('badges')
        .select(`
          *,
          translation_defaults!inner(key, default_value)
        `)
        .or(`community_id.eq.${communityId},is_system_badge.eq.true`)
        .eq('is_active', true)
        .order('name');

      if (error) {
        console.error('Error fetching badges:', error);
        throw error;
      }

      if (!badgeData) return [];

      // Get translation keys for community-specific translations
      const translationKeys = badgeData.map(badge => badge.translation_key).filter(Boolean);
      
      // Fetch community-specific translations if they exist
      const { data: communityTranslations, error: translationsError } = await supabase
        .from('community_translations')
        .select('translation_key, value')
        .eq('community_id', communityId)
        .eq('locale', currentLocale)
        .in('translation_key', translationKeys);

      if (translationsError) {
        console.error('Error fetching badge translations:', translationsError);
      }

      // Create a map of community translation overrides
      const translationOverrides = new Map();
      if (communityTranslations) {
        communityTranslations.forEach(t => {
          translationOverrides.set(t.translation_key, t.value);
        });
      }

      // Map badges with resolved translations
      return badgeData.map((badge: any) => {
        const defaultName = badge.translation_defaults?.default_value || badge.translation_key || badge.name;
        const translatedName = badge.translation_key 
          ? (translationOverrides.get(badge.translation_key) || defaultName)
          : badge.name;

        return {
          ...badge,
          translatedName // Add resolved translation
        };
      });
    },
    enabled: !!communityId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};